import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  TrendingUp, TrendingDown, Activity, AlertTriangle, Shield, 
  Zap, Clock, CheckCircle, XCircle, RefreshCw, Loader2,
  ArrowUpRight, ArrowDownRight, Minus, AlertCircle
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const REGIME_CONFIG = {
  BULLISH_TREND: {
    label: "Bullish Trend Day",
    icon: TrendingUp,
    color: "emerald",
    bgClass: "bg-gradient-to-r from-emerald-900/40 to-emerald-800/20",
    borderClass: "border-emerald-600/50",
    badgeClass: "bg-emerald-600",
    description: "Market trending up, pullbacks holding"
  },
  BEARISH_TREND: {
    label: "Bearish Trend Day",
    icon: TrendingDown,
    color: "red",
    bgClass: "bg-gradient-to-r from-red-900/40 to-red-800/20",
    borderClass: "border-red-600/50",
    badgeClass: "bg-red-600",
    description: "Market trending down, bounces failing"
  },
  RANGE: {
    label: "Range Day",
    icon: Minus,
    color: "amber",
    bgClass: "bg-gradient-to-r from-amber-900/40 to-amber-800/20",
    borderClass: "border-amber-600/50",
    badgeClass: "bg-amber-600",
    description: "Sideways action, mean reversion favored"
  },
  UNCLEAR: {
    label: "Unclear / No Trade Day",
    icon: AlertCircle,
    color: "zinc",
    bgClass: "bg-gradient-to-r from-zinc-800/60 to-zinc-700/40",
    borderClass: "border-zinc-600/50",
    badgeClass: "bg-zinc-600",
    description: "Mixed signals, no clear edge"
  },
  MARKET_CLOSED: {
    label: "Market Closed",
    icon: Clock,
    color: "zinc",
    bgClass: "bg-gradient-to-r from-zinc-800/80 to-zinc-700/60",
    borderClass: "border-zinc-500/50",
    badgeClass: "bg-zinc-500",
    description: "Market is closed - no trading today"
  }
};

const VOLATILITY_CONFIG = {
  NORMAL: { label: "Normal", color: "text-emerald-300 font-semibold", bgColor: "bg-emerald-500/20" },
  ELEVATED: { label: "Elevated", color: "text-amber-300 font-semibold", bgColor: "bg-amber-500/20" },
  HIGH_VOLATILITY: { label: "High", color: "text-red-300 font-semibold", bgColor: "bg-red-500/20" }
};

const RISK_MODE_CONFIG = {
  NORMAL: { label: "Normal Size", color: "text-emerald-300 font-semibold", icon: CheckCircle },
  REDUCED: { label: "75% Size", color: "text-amber-300 font-semibold", icon: AlertTriangle },
  HALF_SIZE: { label: "Half Size", color: "text-red-300 font-semibold", icon: Shield },
  MINIMAL: { label: "No Trade", color: "text-zinc-300 font-semibold", icon: XCircle }
};

export default function MarketRegimeBanner({ onRegimeChange }) {
  const [regime, setRegime] = useState(() => {
    // Load from cache for instant display - 5 min TTL
    try {
      const cached = localStorage.getItem('marketRegime_cache');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < 5 * 60 * 1000) { // 5 min TTL
          return data;
        }
      }
    } catch (e) {}
    return null;
  });
  const [loading, setLoading] = useState(!regime); // No loading if cache exists
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchRegime = async () => {
    // Never show loading if we have cached data
    if (!regime) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/market-regime`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegime(response.data);
      setError(null);
      
      // Cache for instant display (5 min TTL)
      try {
        localStorage.setItem('marketRegime_cache', JSON.stringify({
          data: response.data,
          timestamp: Date.now()
        }));
      } catch (e) {}
      
      if (onRegimeChange) {
        onRegimeChange(response.data);
      }
    } catch (err) {
      if (!regime) setError('Failed to load market regime');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegime();
    // Refresh every 5 minutes
    const interval = setInterval(fetchRegime, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-cyan-500 mr-2" />
          <span className="text-zinc-400">Analyzing market regime...</span>
        </CardContent>
      </Card>
    );
  }

  if (error || !regime) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="flex items-center justify-between py-4">
          <span className="text-zinc-400">{error || 'No regime data'}</span>
          <Button onClick={fetchRegime} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-1" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const config = REGIME_CONFIG[regime.base_regime] || REGIME_CONFIG.UNCLEAR;
  const volatilityConfig = VOLATILITY_CONFIG[regime.volatility_overlay] || VOLATILITY_CONFIG.NORMAL;
  const riskConfig = RISK_MODE_CONFIG[regime.risk_mode] || RISK_MODE_CONFIG.NORMAL;
  const RegimeIcon = config.icon;
  const RiskIcon = riskConfig.icon;

  // Market Closed State - Show simplified view
  if (regime.is_market_closed || regime.base_regime === 'MARKET_CLOSED') {
    return (
      <Card className="bg-zinc-800/80 border-zinc-600 border-2" data-testid="market-regime-banner">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-zinc-600">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-white">Market Closed</span>
                  <Badge className="bg-zinc-600">
                    {regime.closed_reason || 'Weekend'}
                  </Badge>
                </div>
                <p className="text-sm text-zinc-400">No trading signals available - market reopens next trading day</p>
              </div>
            </div>
            <Button onClick={fetchRegime} variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${config.bgClass} ${config.borderClass} border-2`} data-testid="market-regime-banner">
      <CardContent className="p-3 sm:p-4">
        {/* Premarket Warning Banner */}
        {regime.premarket?.active && regime.premarket_warning && (
          <div className="mb-3 p-2 rounded-lg bg-amber-500/20 border border-amber-500/50 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <span className="text-xs sm:text-sm text-amber-200">{regime.premarket_warning}</span>
          </div>
        )}
        
        {/* Premarket Data Banner */}
        {regime.premarket?.active && (
          <div className="mb-3 p-2 rounded-lg bg-blue-500/20 border border-blue-500/50 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-600 text-xs">PRE-MARKET</Badge>
              <span className="text-xs text-blue-200">Live premarket data</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className={`flex items-center gap-1 font-mono font-medium ${regime.premarket.spy_change >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                SPY {regime.premarket.spy_change >= 0 ? '+' : ''}{regime.premarket.spy_change}%
              </span>
              <span className={`flex items-center gap-1 font-mono font-medium ${regime.premarket.qqq_change >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                QQQ {regime.premarket.qqq_change >= 0 ? '+' : ''}{regime.premarket.qqq_change}%
              </span>
            </div>
          </div>
        )}
        
        {/* Main Header Row */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-3 sm:gap-4">
          {/* Regime Label */}
          <div className="flex items-center gap-2 sm:gap-3">
            <div className={`p-1.5 sm:p-2 rounded-lg ${config.badgeClass}`}>
              <RegimeIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-base sm:text-lg font-bold text-white">{config.label}</span>
                <Badge className={`${config.badgeClass} text-xs`}>
                  {regime.confidence}% Confidence
                </Badge>
                {regime.premarket?.active && (
                  <Badge variant="outline" className="text-xs border-blue-400 text-blue-300">
                    Prior Close
                  </Badge>
                )}
              </div>
              <p className="text-xs sm:text-sm text-white/80">{config.description}</p>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            {/* Market Changes - show premarket or regular */}
            <div className="flex items-center gap-2 sm:gap-3 text-sm bg-black/30 px-2 sm:px-3 py-1.5 rounded-lg">
              <span className={`flex items-center gap-1 font-medium ${(regime.premarket?.active ? regime.premarket.spy_change : regime.market_data?.spy_change) >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                {(regime.premarket?.active ? regime.premarket.spy_change : regime.market_data?.spy_change) >= 0 ? <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4" /> : <ArrowDownRight className="w-3 h-3 sm:w-4 sm:h-4" />}
                <span className="text-xs sm:text-sm">SPY {(regime.premarket?.active ? regime.premarket.spy_change : regime.market_data?.spy_change) >= 0 ? '+' : ''}{regime.premarket?.active ? regime.premarket.spy_change : regime.market_data?.spy_change}%</span>
              </span>
              <span className={`flex items-center gap-1 font-medium ${(regime.premarket?.active ? regime.premarket.qqq_change : regime.market_data?.qqq_change) >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                {(regime.premarket?.active ? regime.premarket.qqq_change : regime.market_data?.qqq_change) >= 0 ? <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4" /> : <ArrowDownRight className="w-3 h-3 sm:w-4 sm:h-4" />}
                <span className="text-xs sm:text-sm">QQQ {(regime.premarket?.active ? regime.premarket.qqq_change : regime.market_data?.qqq_change) >= 0 ? '+' : ''}{regime.premarket?.active ? regime.premarket.qqq_change : regime.market_data?.qqq_change}%</span>
              </span>
            </div>

            {/* VIX & Volatility */}
            <div className={`px-2 sm:px-3 py-1.5 rounded-lg bg-black/30`}>
              <span className={`text-xs sm:text-sm font-semibold ${volatilityConfig.color}`}>
                VIX: {regime.market_data?.vix} ({volatilityConfig.label})
              </span>
            </div>

            {/* Risk Mode */}
            <div className="flex items-center gap-1 bg-black/30 px-2 sm:px-3 py-1.5 rounded-lg">
              <RiskIcon className={`w-3 h-3 sm:w-4 sm:h-4 ${riskConfig.color}`} />
              <span className={`text-xs sm:text-sm font-semibold ${riskConfig.color}`}>
                {riskConfig.label}
              </span>
            </div>

            {/* Refresh Button */}
            <Button onClick={fetchRegime} variant="ghost" size="sm" className="text-zinc-400 hover:text-white p-1 sm:p-2">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Expand/Collapse Toggle */}
        <button 
          onClick={() => setExpanded(!expanded)}
          className="w-full text-center text-xs text-white/70 mt-3 hover:text-white font-medium bg-black/20 rounded py-1"
        >
          {expanded ? '▲ Hide Details' : '▼ Show Allowed & Avoid Setups'}
        </button>

        {/* Expandable Details */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-zinc-700/50 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Best Setups Today */}
            <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <span className="font-medium text-emerald-400">Best Setups Today</span>
              </div>
              <ul className="space-y-1">
                {regime.allowed_setups?.map((setup, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                    <span className="text-emerald-400 mt-0.5">•</span>
                    {setup}
                  </li>
                ))}
              </ul>
            </div>

            {/* Avoid Today */}
            <div className="bg-red-900/20 border border-red-700/40 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <XCircle className="w-5 h-5 text-red-400" />
                <span className="font-medium text-red-400">Avoid Today</span>
              </div>
              <ul className="space-y-1">
                {regime.avoid_setups?.map((setup, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                    <span className="text-red-400 mt-0.5">•</span>
                    {setup}
                  </li>
                ))}
              </ul>
            </div>

            {/* Why This Classification */}
            <div className="md:col-span-2 bg-zinc-800/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-5 h-5 text-cyan-400" />
                <span className="font-medium text-cyan-400">Why This Classification</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {regime.reasons?.map((reason, idx) => (
                  <Badge key={idx} variant="outline" className="text-zinc-300 border-zinc-600 text-xs">
                    {reason}
                  </Badge>
                ))}
              </div>
              
              {/* Warnings */}
              {regime.warnings?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-zinc-700/50">
                  <div className="flex items-center gap-2 text-amber-400 text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="font-medium">Warnings:</span>
                  </div>
                  <ul className="mt-1 space-y-1">
                    {regime.warnings.map((warning, idx) => (
                      <li key={idx} className="text-sm text-amber-300 ml-6">• {warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Risk Advice */}
              <div className="mt-3 pt-3 border-t border-zinc-700/50">
                <div className="flex items-center gap-2">
                  <Shield className={`w-4 h-4 ${riskConfig.color}`} />
                  <span className={`text-sm ${riskConfig.color}`}>{regime.risk_advice}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
